import React, { createContext, useContext, useEffect, useState } from "react";
import { api, getToken, setToken } from "@/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      setToken(null);
      setUser(false);
      return null;
    }
  };

  useEffect(() => {
    if (!getToken()) {
      setUser(false);
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, []);

  // Quando o api.js detecta 401 em request autenticada (token zumbi após
  // deploy/JWT_SECRET mudar), limpa o user para redirecionar pro login.
  useEffect(() => {
    const onInvalidated = () => setUser(false);
    window.addEventListener("auth:invalidated", onInvalidated);
    return () => window.removeEventListener("auth:invalidated", onInvalidated);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) setToken(data.token);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
  };

  const impersonate = async (tenantId, password) => {
    const { data } = await api.post(`/master/tenants/${tenantId}/impersonate`, { password });
    if (data.token) setToken(data.token);
    await refresh();
  };

  const exitImpersonation = async () => {
    const { data } = await api.post(`/master/exit-impersonation`);
    if (data.token) setToken(data.token);
    await refresh();
  };

  const isMaster = user && user.role === "master";
  const isImpersonating = user && user.impersonating;
  const isOwner = user && (user.role === "owner" || (isMaster && isImpersonating));
  const role = user?.role;
  const canSeeFinance = user && ["master", "owner", "gerente"].includes(user.role);
  const isTecnico = user && user.role === "tecnico";
  const isMarketingCRM = user && user.role === "marketing_crm";
  // Quem pode CRIAR/EDITAR/EXCLUIR produtos:
  // owner/gerente/master sempre; colaborador só se tem flag explícita.
  const canManageProducts = !!user && (
    ["master", "owner", "gerente"].includes(user.role)
    || (user.role === "colaborador" && !!user.can_create_products)
  );
  // Quem pode editar o CRM: todos exceto marketing_crm sem flag crm_can_edit.
  const canEditCRM = !!user && (
    user.role !== "marketing_crm" || !!user.crm_can_edit
  );

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, refresh,
      impersonate, exitImpersonation,
      isMaster, isImpersonating, isOwner, role, canSeeFinance, isTecnico,
      isMarketingCRM, canManageProducts, canEditCRM,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
